import {
  ALLOWED_IMAGE_CONTENT_TYPES,
  type AllowedImageContentType,
  MAX_IMAGE_UPLOAD_BYTES,
} from '@repo/api-contracts';
import { useState } from 'react';

export interface PresignedTarget {
  readonly uploadUrl: string;
  readonly key: string;
}

interface UsePresignedImageUploadResult {
  /** 성공하면 등록·교체 요청에 넘길 S3 키, 실패하면 null. */
  readonly upload: (file: File) => Promise<string | null>;
  readonly isUploading: boolean;
  readonly error: string | null;
  /** 지난 업로드 오류를 지운다 — 폼을 다시 열 때 옛 오류가 남지 않게. */
  readonly clearError: () => void;
}

const describeCause = (cause: unknown): string =>
  cause instanceof Error && cause.message.length > 0 ? cause.message : '알 수 없는 오류';

/**
 * 이미지 파일을 서명된 PUT URL 로 S3 에 직접 올린다 — 발급 함수만 바꿔 여러 화면이 쓴다.
 * 크기·형식은 presigned PUT 이 강제하지 못해 여기서 먼저 거른다(서버도 등록 시 다시 확인).
 */
export const usePresignedImageUpload = (
  presign: (contentType: AllowedImageContentType) => Promise<PresignedTarget>,
): UsePresignedImageUploadResult => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (file: File): Promise<string | null> => {
    if (!(ALLOWED_IMAGE_CONTENT_TYPES as readonly string[]).includes(file.type)) {
      setError('jpeg · png · webp 형식만 업로드할 수 있습니다.');
      return null;
    }

    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      setError('이미지는 10MB 이하여야 합니다.');
      return null;
    }

    setIsUploading(true);
    setError(null);

    try {
      const presigned = await presign(file.type as AllowedImageContentType);
      const response = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file,
      });

      if (!response.ok) {
        setError(`S3 업로드에 실패했습니다 (HTTP ${response.status}). 다시 시도해 주세요.`);
        return null;
      }

      return presigned.key;
    } catch (cause) {
      setError(`업로드에 실패했습니다: ${describeCause(cause)}. 다시 시도해 주세요.`);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  return { upload, isUploading, error, clearError: () => setError(null) };
};
